import { Grid, Hidden, makeStyles, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@material-ui/core";
import { Field, FieldProvider, Text, Numeric, DatePicker, FuzzySelect, FormLabel } from "@ngt/forms";
import { useFormActions, useScopedField } from "@ngt/forms-core";
import React, { FunctionComponent, useEffect } from "react";
import * as Dtos from '../../api/dtos';
import FormContainer from "../../components/layouts/FormContainer";
import { fieldMargin, compactGridFieldMargin } from "../../constants/forms";
import { DOLLAR_PREFIX } from "../../constants/inputAdornments";
import { INTEGER_FORMAT, MONEY_FORMAT } from "../../constants/numberFormats";

interface IBudgetTableProps {
    code: string;
    name: string;
    fields: Array<{ code: string; name: string; }>;
}

const useTableStyles = makeStyles(theme => ({
    paper: {
        margin: theme.spacing(2, 0, 2, 0)
    },
    code: {
        color: theme.palette.primary.main,
        width: 120,
        maxWidth: 120,
        minWidth: 120
    },
    name: {
        color: theme.palette.primary.main
    },
    budget: {
        color: theme.palette.primary.main,
        width: 180,
        maxWidth: 180,
        minWidth: 180
    }
}));

const BudgetTable: FunctionComponent<IBudgetTableProps> = ({
    code,
    fields,
    name
}) => {
    const classes = useTableStyles();

    return (
        <TableContainer component={Paper} className={classes.paper}>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell className={classes.code}><strong>{code}</strong></TableCell>
                        <TableCell className={classes.name}><strong>{name}</strong></TableCell>
                        <TableCell align="right" className={classes.budget}><strong>Original Budget</strong></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {fields && fields.map((row) => (
                        <FieldProvider key={row.name} name={row.name} autoRegister={false}>
                            <TableRow>
                                <TableCell scope="row">
                                    {row.code}
                                </TableCell>
                                <TableCell>
                                    <FormLabel />
                                </TableCell>
                                <TableCell>
                                    <Field
                                        name={row.name}
                                        component={Numeric}
                                        numberFormat={MONEY_FORMAT}
                                        margin="none"
                                        label={null}
                                        FieldProps={{
                                            prependParentName: false
                                        }}
                                        InputProps={DOLLAR_PREFIX}
                                    />
                                </TableCell>
                            </TableRow>
                        </FieldProvider>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

const landAcquisitionCostsProps: IBudgetTableProps = {
    code: "T010000",
    name: "Land Acquisition Costs",
    fields: [
        {
            code: "T010010",
            name: "laLandAcquisition"
        },
        {
            code: "T010020",
            name: "laStampDuty"
        },
        {
            code: "T010030",
            name: "laLegalFeesLandPurchasesCostOfLand"
        },
        {
            code: "T010040",
            name: "laMiscellaneousAcquisitionCost"
        },
        {
            code: "T010050",
            name: "laPexaFee"
        },
        {
            code: "T010060",
            name: "laFinanceFees"
        },
        {
            code: "T010070",
            name: "laPenaltyInterest"
        },
        {
            code: "T010080",
            name: "laCouncilRatesOnSettlement"
        },
        {
            code: "T010090",
            name: "laWaterRatesOnSettlement"
        },
        {
            code: "T010100",
            name: "laLandTaxOnSettlement"
        },
        {
            code: "T020000",
            name: "laStampDutyOnLandHoldings"
        }
    ]
}

const salesIncomeProps: IBudgetTableProps = {
    code: "T110000",
    name: "Sales Income",
    fields: [
        {
            code: "T110010",
            name: "siSalesPriceNet"
        },
        {
            code: "T110020",
            name: "siInterestIncome"
        },
        {
            code: "T110030",
            name: "siForfeitedDeposits"
        },
    ]
}

const salesExpenseProps: IBudgetTableProps = {
    code: "T120000",
    name: "Sales Expenses",
    fields: [
        {
            code: "T120020",
            name: "salesExpensesLegalFees"
        },
        {
            code: "T120040",
            name: "salesCommission"
        },
        {
            code: "T120050",
            name: "internalAgentsCommission"
        },
        {
            code: "T120060",
            name: "salesExpensesCouncilRates"
        },
        {
            code: "T120080",
            name: "salesExpensesWaterRates"
        },
        {
            code: "T120100",
            name: "salesExpensesLandTax"
        },
        {
            code: "T120120",
            name: "salesExpensesFinanceFees"
        },
        {
            code: "T120140",
            name: "interestPaid"
        },
        {
            code: "T120150",
            name: "pexaFee"
        },
        {
            code: "T120160",
            name: "seStrataCommunityManagementFee"
        },
        {
            code: "T120170",
            name: "seMiscellaneousSellingCosts"
        }
    ]
}

const rentalIncomeProps: IBudgetTableProps = {
    code: "T130000",
    name: "Rental Income",
    fields: [
        {
            code: "T130010",
            name: "riRentReceived"
        },
        {
            code: "T130020",
            name: "riOutgoingsIncome"
        },
    ]
}

const otherIncomeProps: IBudgetTableProps = {
    code: "T140000",
    name: "Other Income",
    fields: [
        {
            code: "T140010",
            name: "oiInfrastructureContribution"
        },
        {
            code: "T140020",
            name: "oiOptionFees"
        },
        {
            code: "T140030",
            name: "oiOtherIncome"
        },
        {
            code: "T140040",
            name: "oiLeaseIncome"
        }
    ]
}

const realEstateCommissionProps: IBudgetTableProps = {
    code: "T150000",
    name: "Real Estate Commission",
    fields: [
        {
            code: "T150010",
            name: "recSalesCommissionInternal"
        },
        {
            code: "T150020",
            name: "recSalesCommissionExternal"
        },
    ]
}

const stateAuthorityProps: IBudgetTableProps = {
    code: "T210000",
    name: "Statutory Authority Fees",
    fields: [
        {
            code: "T210010",
            name: "localGovernmentFees"
        },
        {
            code: "T210020",
            name: "stateGovernmentFees"
        },
        {
            code: "T210030",
            name: "federalGovernmentFees"
        },
        {
            code: "T210040",
            name: "landAndPropertyInformationFees"
        },
        {
            code: "T210050",
            name: "sectionNinetyFourContributions"
        },
        {
            code: "T210060",
            name: "stateInfrastructureContributions"
        },
        {
            code: "T120065",
            name: "safBiodiversityConservationFund"
        },
        {
            code: "T210070",
            name: "stateAuthorityMiscellaneous"
        }
    ]
}

const supplyAuthorityProps: IBudgetTableProps = {
    code: "T220000",
    name: "Supply Authority Fees",
    fields: [
        {
            code: "T220010",
            name: "sewerAndWater"
        },
        {
            code: "T220020",
            name: "power"
        },
        {
            code: "T220030",
            name: "communications"
        },
        {
            code: "T220040",
            name: "gas"
        },
        {
            code: "T220050",
            name: "supplyAuthorityMiscellaneous"
        }
    ]
}

const professionalProps: IBudgetTableProps = {
    code: "T230000",
    name: "Professional Fees",
    fields: [
        {
            code: "T230010",
            name: "architect"
        },
        {
            code: "T230020",
            name: "structuralEngineer"
        },
        {
            code: "T230030",
            name: "civilEngineer"
        },
        {
            code: "T230040",
            name: "electricalEngineer"
        },
        {
            code: "T230050",
            name: "hydraulicEngineer"
        },
        {
            code: "T230060",
            name: "mechanicalEngineer"
        },
        {
            code: "T230070",
            name: "geotechnicalEngineer"
        },
        {
            code: "T230080",
            name: "communicationEngineer"
        },
        {
            code: "T230090",
            name: "surveyor"
        },
        {
            code: "T230100",
            name: "townPlanner"
        },
        {
            code: "T230110",
            name: "trafficEngineer"
        },
        {
            code: "T230120",
            name: "acousticEngineer"
        },
        {
            code: "T230130",
            name: "environmentalConsultant"
        },
        {
            code: "T230140",
            name: "floraAndFaunaConsultant"
        },
        {
            code: "T230150",
            name: "bushFireConsultant"
        },
        {
            code: "T230160",
            name: "landscapeArchitect"
        },
        {
            code: "T230170",
            name: "projectManager"
        },
        {
            code: "T230180",
            name: "valuerOrMarketAnalyst"
        },
        {
            code: "T230190",
            name: "strataOrCommunityTitleManager"
        },
        {
            code: "T230200",
            name: "pcaOrBcaSurveyor"
        },
        {
            code: "T230210",
            name: "superintendent"
        },
        {
            code: "T230215",
            name: "heritageConsultant"
        },
        {
            code: "T230220",
            name: "professionalMiscellaneous"
        },
        {
            code: "T230230",
            name: "businessAnalysis"
        },
        {
            code: "T230250",
            name: "frameworkImplementation"
        }
    ]
}



const constructionWorksProps: IBudgetTableProps = {
    code: "T240000",
    name: "Construction Works",
    fields: [
        {
            code: "T240010",
            name: "demolition"
        },
        {
            code: "T240020",
            name: "civilWorks"
        },
        {
            code: "T240030",
            name: "buildingWorks"
        },
        {
            code: "T240040",
            name: "roadOrIntersectionConstruction"
        },
        {
            code: "T240050",
            name: "powerConstruction"
        },
        {
            code: "T240060",
            name: "waterConstruction"
        },
        {
            code: "T240070",
            name: "sewerConstruction"
        },
        {
            code: "T240080",
            name: "communicationsConstruction"
        },
        {
            code: "T240090",
            name: "otherServices"
        },
        {
            code: "T240100",
            name: "fencing"
        },
        {
            code: "T240110",
            name: "clearingOrSlashingOrMulching"
        },
        {
            code: "T240120",
            name: "streetTreePlanting"
        },
        {
            code: "T240130",
            name: "landscapingAndEstablishment"
        },
        {
            code: "T240140",
            name: "openSpaceEmbellishments"
        },
        {
            code: "T240150",
            name: "constructionContingency"
        },
        {
            code: "T240160",
            name: "constructionWorksMiscellaneous"
        },
        {
            code: "T240170",
            name: "clientSuppliedMaterial"
        }
    ]
}

const marketingProps: IBudgetTableProps = {
    code: "T250000",
    name: "Marketing",
    fields: [
        {
            code: "T250010",
            name: "marketingConsultant"
        },
        {
            code: "T250020",
            name: "advertising"
        },
        {
            code: "T250030",
            name: "signage"
        },
        {
            code: "T250040",
            name: "website"
        },
        {
            code: "T250050",
            name: "promotionalMaterial"
        },
        {
            code: "T250060",
            name: "events"
        },
        {
            code: "T250070",
            name: "sponsorships"
        },
        {
            code: "T250080",
            name: "referals"
        },
        {
            code: "T250090",
            name: "marketingMiscellaneous"
        },
        {
            code: "T250999",
            name: "marketingSuspense"
        }
    ]
}

const salesProps: IBudgetTableProps = {
    code: "T260000",
    name: "Sales",
    fields: [
        {
            code: "T260010",
            name: "salesCentreRAndM"
        },
        {
            code: "T260020",
            name: "salesCentreConsumerables"
        },
        {
            code: "T260030",
            name: "estatePresentation"
        },
        {
            code: "T260040",
            name: "membershipsOrNetworkingEvents"
        },
        {
            code: "T260050",
            name: "salesMiscellaneous"
        },
        {
            code: "T260060",
            name: "telephone"
        },
        {
            code: "T260070",
            name: "utilities"
        },
        {
            code: "T260080",
            name: "rent"
        },
        {
            code: "T260999",
            name: "salesOfficeSuspense"
        }
    ]
}

const projectManagementProps: IBudgetTableProps = {
    code: "T270000",
    name: "Project Management",
    fields: [
        {
            code: "T270010",
            name: "developmentManagementFee"
        },
        {
            code: "T270020",
            name: "projectManagementLegalFees"
        },
        {
            code: "T270030",
            name: "projectManagementFinanceFees"
        },
        {
            code: "T270035",
            name: "pmProjectInterest"
        },
        {
            code: "T270040",
            name: "printingAndStationary"
        },
        {
            code: "T270050",
            name: "security"
        },
        {
            code: "T270060",
            name: "travelAndAccommodation"
        },
        {
            code: "T270070",
            name: "projectContingency"
        },
        {
            code: "T270080",
            name: "projectManagementLandTax"
        },
        {
            code: "T270090",
            name: "projectManagementCouncilRates"
        },
        {
            code: "T270100",
            name: "projectManagementWaterRates"
        },
        {
            code: "T270110",
            name: "strataOrCommunityManagementFees"
        },
        {
            code: "T270120",
            name: "otherRatesAndTaxes"
        },
        {
            code: "T270130",
            name: "projectManagementMiscellaneous"
        },
        {
            code: "T270140",
            name: "gasAndElectricityUsage"
        },
        {
            code: "T270150",
            name: "pmCommunicationsUsage"
        },
        {
            code: "T270160",
            name: "pmInsurance"
        }
    ]
}

const propertyImprovementsProps: IBudgetTableProps = {
    code: "T738000",
    name: "Property Improvements",
    fields: [
        {
            code: "T738010",
            name: "piPropertyImprovements"
        }
    ]
}

interface INewJobRequestProps { }

const useStyles = makeStyles(theme => ({
    paper: {
        padding: theme.spacing(2, 2, 2, 2),
        margin: theme.spacing(2, 0, 2, 0)
    },
    subHeading: {
        padding: theme.spacing(0, 0, 0, 0),
        margin: theme.spacing(3, 0, 0, 0)
    },
    row: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2)
    },
    column: {
        '& > :first-child': {
            margin: theme.spacing(0, 0, 2, 0)
        },

        '& > :last-child': {
            margin: theme.spacing(2, 0, 0, 0)
        }
    }
}));

// const valuesSubscription = { values: true };

//const jobNumberFilterParams: ILookupFilterProps<Dtos.NewJobRequest> = {
//    subscription: valuesSubscription,
//    filterFunction: (formState, _, lookup) => {
//        return {
//            propertyName: lookup?.propertyName ?? '',
//            items: lookup?.items.filter(item => {
//                return formState.values.projectId !== undefined &&
//                    (item as Dtos.LookupItem).parent === formState.values.projectId
//            }) ?? []
//        }
//    }
//}

//const stageNumberFilterParams: ILookupFilterProps<Dtos.NewJobRequest> = {
//    subscription: valuesSubscription,
//    filterFunction: (formState, _, lookup) => {
//        return {
//            propertyName: lookup?.propertyName ?? '',
//            items: lookup?.items.filter(item => {
//                return formState.values.projectId !== undefined &&
//                    (item as Dtos.LookupItem).parent == formState.values.projectId
//            }) ?? []
//        }
//    }
//}

const subscription = {};

const TotalsUpdater: FunctionComponent = () => {
    const { subscribe } = useFormActions<Dtos.NewJobRequest, Dtos.IValidationError>();
    const { context: { setValue: setTotalOriginalBudget } } = useScopedField<Dtos.NewJobRequest['totalOriginalBudget'], Dtos.IValidationError>('totalOriginalBudget', subscription);
    const { context: { setValue: setCostPerLot } } = useScopedField<Dtos.NewJobRequest['costPerLot'], Dtos.IValidationError>('costPerLot', subscription);

    useEffect(() => {
        const unsubscribe = subscribe(
            formState => {
                // const { items } = formState.values;

                const values = formState.values;

                const totalAmount =
                    (values.laLandAcquisition ?? 0) +
                    (values.laStampDuty ?? 0) +
                    (values.laLegalFeesLandPurchasesCostOfLand ?? 0) +
                    (values.laMiscellaneousAcquisitionCost ?? 0) +
                    (values.laPexaFee ?? 0) +
                    (values.laFinanceFees ?? 0) +
                    (values.laPenaltyInterest ?? 0) +
                    (values.laCouncilRatesOnSettlement ?? 0) +
                    (values.laWaterRatesOnSettlement ?? 0) +
                    (values.laLandTaxOnSettlement ?? 0) +
                    (values.laStampDutyOnLandHoldings ?? 0) +
                    (values.salesExpensesLegalFees ?? 0) -
                    //Subtract sales income
                    (values.siSalesPriceNet ?? 0) -
                    (values.siInterestIncome ?? 0) -
                    (values.siForfeitedDeposits ?? 0) +
                    (values.seStrataCommunityManagementFee ?? 0) -
                    (values.seMiscellaneousSellingCosts ?? 0) -
                    //Subtract rental income
                    (values.riRentReceived ?? 0) -
                    (values.riOutgoingsIncome ?? 0) +
                    //Subtract other income
                    (values.oiInfrastructureContribution ?? 0) +
                    (values.oiOptionFees ?? 0) -
                    (values.oiOtherIncome ?? 0) -
                    (values.oiLeaseIncome ?? 0) +
                    (values.recSalesCommissionInternal ?? 0) +
                    (values.recSalesCommissionExternal ?? 0) +
                    (values.safBiodiversityConservationFund ?? 0) +
                    (values.businessAnalysis ?? 0) +
                    (values.frameworkImplementation ?? 0) +
                    (values.clientSuppliedMaterial ?? 0) +
                    (values.marketingSuspense ?? 0) +
                    (values.salesOfficeSuspense ?? 0) +
                    (values.pmProjectInterest ?? 0) +
                    (values.pmCommunicationsUsage ?? 0) +
                    (values.pmInsurance ?? 0) +
                    (values.piPropertyImprovements ?? 0) +
                    (values.salesCommission ?? 0) +
                    (values.internalAgentsCommission ?? 0) +
                    (values.salesExpensesCouncilRates ?? 0) +
                    (values.salesExpensesWaterRates ?? 0) +
                    (values.salesExpensesLandTax ?? 0) +
                    (values.salesExpensesFinanceFees ?? 0) +
                    (values.interestPaid ?? 0) +
                    (values.pexaFee ?? 0) +
                    (values.localGovernmentFees ?? 0) +
                    (values.stateGovernmentFees ?? 0) +
                    (values.federalGovernmentFees ?? 0) +
                    (values.landAndPropertyInformationFees ?? 0) +
                    (values.sectionNinetyFourContributions ?? 0) +
                    (values.stateInfrastructureContributions ?? 0) +
                    (values.stateAuthorityMiscellaneous ?? 0) +
                    (values.sewerAndWater ?? 0) +
                    (values.power ?? 0) +
                    (values.communications ?? 0) +
                    (values.gas ?? 0) +
                    (values.supplyAuthorityMiscellaneous ?? 0) +
                    (values.architect ?? 0) +
                    (values.structuralEngineer ?? 0) +
                    (values.civilEngineer ?? 0) +
                    (values.electricalEngineer ?? 0) +
                    (values.hydraulicEngineer ?? 0) +
                    (values.mechanicalEngineer ?? 0) +
                    (values.geotechnicalEngineer ?? 0) +
                    (values.communicationEngineer ?? 0) +
                    (values.surveyor ?? 0) +
                    (values.townPlanner ?? 0) +
                    (values.trafficEngineer ?? 0) +
                    (values.acousticEngineer ?? 0) +
                    (values.environmentalConsultant ?? 0) +
                    (values.floraAndFaunaConsultant ?? 0) +
                    (values.bushFireConsultant ?? 0) +
                    (values.landscapeArchitect ?? 0) +
                    (values.projectManager ?? 0) +
                    (values.valuerOrMarketAnalyst ?? 0) +
                    (values.strataOrCommunityTitleManager ?? 0) +
                    (values.pcaOrBcaSurveyor ?? 0) +
                    (values.superintendent ?? 0) +
                    (values.heritageConsultant ?? 0) +
                    (values.professionalMiscellaneous ?? 0) +
                    (values.demolition ?? 0) +
                    (values.civilWorks ?? 0) +
                    (values.buildingWorks ?? 0) +
                    (values.roadOrIntersectionConstruction ?? 0) +
                    (values.powerConstruction ?? 0) +
                    (values.waterConstruction ?? 0) +
                    (values.sewerConstruction ?? 0) +
                    (values.communicationsConstruction ?? 0) +
                    (values.otherServices ?? 0) +
                    (values.fencing ?? 0) +
                    (values.clearingOrSlashingOrMulching ?? 0) +
                    (values.streetTreePlanting ?? 0) +
                    (values.landscapingAndEstablishment ?? 0) +
                    (values.openSpaceEmbellishments ?? 0) +
                    (values.constructionContingency ?? 0) +
                    (values.constructionWorksMiscellaneous ?? 0) +
                    (values.marketingConsultant ?? 0) +
                    (values.advertising ?? 0) +
                    (values.signage ?? 0) +
                    (values.website ?? 0) +
                    (values.promotionalMaterial ?? 0) +
                    (values.events ?? 0) +
                    (values.sponsorships ?? 0) +
                    (values.referals ?? 0) +
                    (values.marketingMiscellaneous ?? 0) +
                    (values.salesCentreRAndM ?? 0) +
                    (values.salesCentreConsumerables ?? 0) +
                    (values.estatePresentation ?? 0) +
                    (values.membershipsOrNetworkingEvents ?? 0) +
                    (values.salesMiscellaneous ?? 0) +
                    (values.telephone ?? 0) +
                    (values.utilities ?? 0) +
                    (values.rent ?? 0) +
                    (values.developmentManagementFee ?? 0) +
                    (values.projectManagementLegalFees ?? 0) +
                    (values.projectManagementFinanceFees ?? 0) +
                    (values.printingAndStationary ?? 0) +
                    (values.security ?? 0) +
                    (values.travelAndAccommodation ?? 0) +
                    (values.projectContingency ?? 0) +
                    (values.projectManagementLandTax ?? 0) +
                    (values.projectManagementCouncilRates ?? 0) +
                    (values.projectManagementWaterRates ?? 0) +
                    (values.strataOrCommunityManagementFees ?? 0) +
                    (values.otherRatesAndTaxes ?? 0) +
                    (values.projectManagementMiscellaneous ?? 0) +
                    (values.gasAndElectricityUsage ?? 0);

                setTotalOriginalBudget(totalAmount, false, true, true);
                setCostPerLot(totalAmount / (values.numberOfLots ? values.numberOfLots : 1), false, true, true);
            },
            {
                values: true,
                dirty: false,
                errors: false,
                fields: false,
                focused: false,
                initialValues: false,
                submitting: false,
                touched: false,
                validating: false
            }
        );

        return () => {
            unsubscribe();
        }
    }, [setTotalOriginalBudget, setCostPerLot, subscribe]);

    return null;
}

const NewJobRequest: React.FunctionComponent<INewJobRequestProps> = () => {

    const classes = useStyles();

    const authorisations = (
        <>
            <Typography
                variant="h3"
                color="secondary"
                className={classes.subHeading}
            >
                Authorisations
            </Typography>
            <Paper className={classes.paper}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={8}>
                        <Field
                            name="enteredBy"
                            component={Text}
                            margin={compactGridFieldMargin}
                            disabled
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Field
                            name="enteredDate"
                            component={DatePicker}
                            margin={compactGridFieldMargin}
                            disabled
                        />
                    </Grid>
                </Grid>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={8}>
                        <Field
                            name="approved"
                            component={FuzzySelect}
                            margin={compactGridFieldMargin}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Field
                            name="approvedDate"
                            component={DatePicker}
                            margin={compactGridFieldMargin}
                        />
                    </Grid>
                </Grid>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={8}>
                        <Field
                            name="authorised"
                            component={FuzzySelect}
                            margin={compactGridFieldMargin}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Field
                            name="authorisedDate"
                            component={DatePicker}
                            margin={compactGridFieldMargin}
                        />
                    </Grid>
                </Grid>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={8}>
                        <Field
                            name="finalised"
                            component={Text}
                            margin={compactGridFieldMargin}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Field
                            name="finalisedDate"
                            component={DatePicker}
                            margin={compactGridFieldMargin}
                        />
                    </Grid>
                </Grid>
            </Paper>
        </>
    );

    return (
        <FormContainer
            formType={ Dtos.NewJobRequest }
        >
            { /* FieldProvider is placed here so form level errors will appear on the form. */}
            <FieldProvider name="id" />
            <Grid container spacing={2} className={classes.row}>
                <Grid item xs={12} lg={6} className={classes.column}>
                    <Paper className={classes.paper}>
                        <Field
                            name="projectId"
                            component={FuzzySelect}
                            margin={fieldMargin}
                        />
                        <Field
                            name="jobCode"
                            // component={FuzzySelect}
                            component={Text}
                            margin={fieldMargin}
                            // LookupFilterProps={jobNumberFilterParams}
                        />
                        <Field
                            name="stageNumber"
                            // component={FuzzySelect}
                            component={Text}
                            margin={fieldMargin}
                            // LookupFilterProps={stageNumberFilterParams}
                        />
                        <Field
                            name="numberOfLots"
                            component={Numeric}
                            numberFormat={INTEGER_FORMAT}
                            margin={fieldMargin}
                        />
                        {/*<Field*/}
                        {/*    name="date"*/}
                        {/*    component={DatePicker}*/}
                        {/*    margin={fieldMargin}*/}
                        {/*/>*/}
                    </Paper>
                </Grid>
            </Grid>
            <Grid container spacing={2}>
                <Grid item xs={12} lg={6} className={classes.column}>
                    <BudgetTable
                        {...landAcquisitionCostsProps}
                    />
                    <BudgetTable
                        {...salesIncomeProps}
                    />
                    <BudgetTable
                        {...salesExpenseProps}
                    />
                    <BudgetTable
                        {...rentalIncomeProps}
                    />
                    <BudgetTable
                        {...otherIncomeProps}
                    />
                    <BudgetTable
                        {...realEstateCommissionProps}
                    />
                    <BudgetTable
                        {...stateAuthorityProps}
                    />
                    <BudgetTable
                        {...marketingProps}
                    />
                    <BudgetTable
                        {...salesProps}
                    />
                    <Hidden mdDown>
                        {authorisations}
                    </Hidden>
                </Grid>
                <Grid item xs={12} lg={6} className={classes.column}>
                    <BudgetTable
                        {...supplyAuthorityProps}
                    />
                    <BudgetTable
                        {...professionalProps}
                    />
                    <BudgetTable
                        {...constructionWorksProps}
                    />
                    <BudgetTable
                        {...projectManagementProps}
                    />
                    <BudgetTable
                        {...propertyImprovementsProps}
                    />
                    <Paper className={classes.paper}>
                        <TotalsUpdater />
                        <Field
                            name="totalOriginalBudget"
                            component={Numeric}
                            numberFormat={MONEY_FORMAT}
                            margin={fieldMargin}
                            InputProps={DOLLAR_PREFIX}
                            disabled
                        />
                        <Field
                            name="costPerLot"
                            component={Numeric}
                            numberFormat={MONEY_FORMAT}
                            margin={fieldMargin}
                            InputProps={DOLLAR_PREFIX}
                            disabled
                        />
                    </Paper>
                </Grid>
            </Grid>
            <Hidden lgUp>
                {authorisations}
            </Hidden>
        </FormContainer>
    );
}

export default NewJobRequest;